import { type ChipProps, darken } from '@mui/material';
import { colorsPalette } from './colorsPalette';
import type { ComponentsVariants, Theme } from '@mui/material/styles';

interface VariantStyles {
    labelColor: string;
    borderColor: string;
    backgroundColor: string;
    backgroundColorHover: string;
    borderColorFocus: string;
}

export type ChipColor = Exclude<ChipProps['color'], undefined | 'default'>;

const getVariantStylesMapping = (theme: Theme): Record<ChipColor, VariantStyles> => ({
    info: {
        labelColor: theme.palette.text.secondary,
        borderColor: colorsPalette.blackAlpha[16],
        borderColorFocus: colorsPalette.blackAlpha[70],
        backgroundColor: colorsPalette.blackSolid[4],
        backgroundColorHover: colorsPalette.blackSolid[8],
    },
    success: {
        labelColor: theme.colors.text.success,
        borderColor: theme.colors.border['success-subtle'],
        borderColorFocus: theme.colors.border['success-bold'],
        backgroundColor: theme.colors.background['success-subtle'],
        backgroundColorHover: theme.colors.background['success-subtle-hover'],
    },
    warning: {
        labelColor: theme.colors.text.warning,
        borderColor: theme.colors.border['warning-subtle'],
        borderColorFocus: theme.colors.border['warning-bold'],
        backgroundColor: theme.colors.background['warning-subtle'],
        backgroundColorHover: theme.colors.background['warning-subtle-hover'],
    },
    error: {
        labelColor: theme.colors.text.danger,
        borderColor: theme.colors.border['danger-subtle'],
        borderColorFocus: theme.colors.border['danger-bold'],
        backgroundColor: theme.colors.background['danger-subtle'],
        backgroundColorHover: theme.colors.background['danger-subtle-hover'],
    },
    secondary: {
        // Known as "Darkblue"
        labelColor: colorsPalette.darkblueSolid[100],
        borderColor: colorsPalette.darkblueAlpha[20],
        borderColorFocus: colorsPalette.darkblueSolid[100],
        backgroundColor: colorsPalette.darkblueSolid[4],
        backgroundColorHover: colorsPalette.darkblueSolid[12],
    },
    primary: {
        labelColor: theme.colors.text.progress,
        borderColor: theme.colors.border['progress-subtle'],
        borderColorFocus: theme.colors.border['progress-bold'],
        backgroundColor: theme.colors.background['progress-subtle'],
        backgroundColorHover: darken(theme.colors.background['progress-subtle'], 0.04),
    },
    promotion: {
        labelColor: colorsPalette.purpleSolid[100],
        borderColor: colorsPalette.purpleAlpha[16],
        borderColorFocus: colorsPalette.purpleSolid[100],
        backgroundColor: colorsPalette.purpleSolid[4],
        backgroundColorHover: colorsPalette.purpleSolid[12],
    },
    pink: {
        labelColor: colorsPalette.pinkCustom.dark,
        borderColor: colorsPalette.pinkAlpha[16],
        borderColorFocus: colorsPalette.pinkSolid[100],
        backgroundColor: colorsPalette.pinkSolid[4],
        backgroundColorHover: colorsPalette.pinkSolid[12],
    },
});

export const getChipVariants = (): ComponentsVariants<Theme>['MuiChip'] => {
    const variants: ComponentsVariants<Theme>['MuiChip'] = [];

    const colors: ChipColor[] = [
        'info',
        'success',
        'warning',
        'error',
        'secondary',
        'primary',
        'promotion',
        'pink',
    ];

    for (const color of colors) {
        variants.push({
            props: { color },
            style: ({ theme }) => {
                const styles = getVariantStylesMapping(theme)[color];

                return {
                    color: styles.labelColor,
                    backgroundColor: styles.backgroundColor,
                    border: `1px solid ${styles.borderColor}`,
                    '&.MuiChip-clickable:hover': {
                        backgroundColor: styles.backgroundColorHover,
                    },
                    '&:focus, &.Mui-focusVisible': {
                        borderColor: styles.borderColorFocus,
                    },
                    '&.Mui-focusVisible': {
                        backgroundColor: styles.backgroundColor,
                        '&.MuiChip-clickable:hover': {
                            backgroundColor: styles.backgroundColorHover,
                        },
                    },
                };
            },
        });
    }

    return variants;
};
